import { IconButton, Stack, TableCell, TableRow } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from '../selectField';
import { EntityType } from '../entities/entityType';
import { useApiContext } from '@/contexts/apiContext';
import SelectGrouped from '../selectGrouped';

type Props = {
  consumedEvent?: any;
  errror?: string;
  onAdd: (consumedEvent: any) => void;
  onDelete: (consumedEvent: any) => void;
  onCancelAdd: () => void;
};

const ConsumedEvent = ({ consumedEvent, errror, onAdd, onDelete, onCancelAdd }: Props) => {
  const apiContext = useApiContext();
  const { t } = useTranslation();

  const [entityTypes, setEntityTypes] = useState([
    { value: EntityType.dataSourceInstance, label: t(`common.entityType.${EntityType.dataSourceInstance}`) },
    { value: EntityType.agentInstance, label: t(`common.entityType.${EntityType.agentInstance}`) },
    { value: EntityType.userAppInstance, label: t(`common.entityType.${EntityType.userAppInstance}`) },
  ]);
  const [entityDefs, setEntityDefs] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [eventDefs, setEventDefs] = useState<any[]>([]);

  const [entityType, setEntityType] = useState<any>(consumedEvent?.entityType);
  const [entityDefId, setEntityDefId] = useState<string>(consumedEvent?.entityDefId);
  const [entityId, setEntityId] = useState<string>(consumedEvent?.entityId);
  const [eventDef, setEventDef] = useState<any>(eventDefs.find((ed) => ed.id === consumedEvent?.eventDefId));

  const [changed, setChanged] = useState(false);

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getEntityDefs = async () => {
      try {
        setLoading(true);
        const response =
          entityType === EntityType.agentInstance
            ? await apiContext.getAgentDefs()
            : entityType === EntityType.dataSourceInstance
              ? await apiContext.getDataSourceDefs()
              : entityType === EntityType.userAppInstance
                ? await apiContext.getUserAppDefs()
                : [];

        const entityDefs = [{ value: '*', label: 'Any type' }];
        entityDefs.push(...response.map((ent) => ({ value: ent.id, label: ent.name })));
        setEntityDefs(entityDefs);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    setEntityDefs([{ value: '*', label: 'Any type' }]);
    setEntities([{ value: '*', label: 'Any instance' }]);
    setEventDefs([]);
    setEntityDefId(consumedEvent?.entityDefId);
    setEntityId(consumedEvent?.entityId);
    setEventDef(eventDefs.find((ed) => ed.id === consumedEvent?.eventDefId));

    if (entityType) {
      getEntityDefs();
    }
  }, [entityType]);

  useEffect(() => {
    const getEntities = async () => {
      try {
        setLoading(true);
        const response =
          entityType === EntityType.agentInstance
            ? await apiContext.getAgentInstancesOfType(entityDefId)
            : entityType === EntityType.dataSourceInstance
              ? await apiContext.getDataSourceInstancesOfType(entityDefId)
              : entityType === EntityType.userAppInstance
                ? await apiContext.getUserAppInstancesOfType(entityDefId)
                : [];
        const entities = [{ value: '*', label: 'Any instance' }];
        entities.push(...response.map((ent) => ({ value: ent.id, label: ent.name })));
        setEntities(entities);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    const getEventDefs = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getEventDefs(entityType, entityDefId);
        const eventDefs = response.map((ent) => ({ id: ent.id, label: ent.eventLabel, eventType: ent.eventType }));
        setEventDefs(eventDefs);
        setEventDef(eventDefs.find((ed) => ed.id === consumedEvent?.eventDefId));
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    setEntities([{ value: '*', label: 'Any instance' }]);
    setEventDefs([]);
    setEntityId(consumedEvent?.entityId);
    setEventDef(eventDefs.find((ed) => ed.id === consumedEvent?.eventDefId));
    if (entityType && entityDefId === '*') {
      setEntityId('*');
    } else if (entityType && entityDefId) {
      getEntities();
    }

    if (entityType && entityDefId) {
      getEventDefs();
    }
  }, [entityType, entityDefId]);

  useEffect(() => {
    setEventDefs([]);
  }, [entityType]);

  const onEntityTypeChange = (entityType: string) => {
    setEntityType(entityType);
    setChanged(true);
  };

  const onEntityDefChange = (entityDefId: string) => {
    setEntityDefId(entityDefId);
    setChanged(true);
  };

  const onEntityChange = (entityId: string) => {
    setEntityId(entityId);
    setChanged(true);
  };

  const onEventDefChange = (eventDef: any) => {
    setEventDef(eventDef);
    setChanged(true);
  };

  const cancelAdd = () => {
    onCancelAdd();
  };

  const addConsumedEvent = () => {
    let hasError = false;
    const errors: any = {};
    if (!entityType) {
      errors.entityType = t('common.errors.requiredField');
      hasError = true;
    }

    if (!entityDefId) {
      errors.entityDefId = t('common.errors.requiredField');
      hasError = true;
    }

    if (!entityId) {
      errors.entityId = t('common.errors.requiredField');
      hasError = true;
    }

    if (!eventDef) {
      errors.eventDefId = t('common.errors.requiredField');
      hasError = true;
    }

    setErrors(errors);
    if (!hasError) {
      onAdd({
        entityType,
        entityDefId,
        entityId,
        eventDefId: eventDef.id,
      });
    }
  };

  const deleteConsumedEvent = () => {
    onDelete(consumedEvent);
  };

  return (
    <>
      {consumedEvent ? (
        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
          <TableCell>{t(`common.entityType.${consumedEvent.entityType}`)}</TableCell>
          <TableCell>{entityDefs.find((ed) => ed.value === consumedEvent.entityDefId)?.label}</TableCell>
          <TableCell>{entities.find((ed) => ed.value === consumedEvent.entityId)?.label}</TableCell>
          <TableCell>{eventDefs.find((ed) => ed.id === consumedEvent.eventDefId)?.label}</TableCell>
          <TableCell>
            <Stack direction={'row'} alignItems={'center'}>
              <IconButton size="small" onClick={() => deleteConsumedEvent()} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </TableCell>
        </TableRow>
      ) : (
        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
          <TableCell align="right">
            <SelectField
              required
              options={entityTypes}
              value={entityType}
              autoFocus
              onChange={onEntityTypeChange}
              error={errors.entityType}
              disabled={consumedEvent}
              fullWidth={true}
            />
          </TableCell>
          <TableCell align="right">
            <SelectField
              required
              options={entityDefs}
              value={entityDefId}
              autoFocus
              onChange={onEntityDefChange}
              error={errors.entityDefId}
              disabled={consumedEvent}
            />
          </TableCell>
          <TableCell align="right">
            <SelectField
              required
              options={entities}
              value={entityId}
              autoFocus
              onChange={onEntityChange}
              error={errors.entityId}
              disabled={consumedEvent}
            />
          </TableCell>
          <TableCell align="right">
            <SelectGrouped
              required
              options={eventDefs}
              value={eventDef}
              autoFocus
              onChange={onEventDefChange}
              error={errors.eventDefId}
              disabled={consumedEvent}
            />
          </TableCell>
          <TableCell align="left">
            <Stack direction={'row'} alignItems={'center'}>
              <IconButton size="small" onClick={addConsumedEvent} color="primary" disabled={!changed}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => cancelAdd()}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ConsumedEvent;
